import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { BodyText, Icon, IconName, Switch, theme } from '@sg/garnish';

import { StripePaymentMethodImage } from '@order/integrations-stripe';

export const BagPaymentMethods = (props: BagPaymentMethodsProps) => {
  const {
    isDisabled,
    flexPaymentMethod,
    shouldUseFlex,
    onChangeFlexUsage,

    shouldUseCredit,
    availableCredit,
    appliedCredit,
    onChangeCreditUsage,

    hasNoPaymentMethod,
    stripePaymentMethod,
    stripePaymentMethodIcon,
    stripePaymentMethodImage,
    onPressStripePaymentMethod,
  } = props;

  const { formatMessage } = useIntl();

  const { applied, available } = useCreditUsageHint(appliedCredit, availableCredit);

  return (
    <View style={styles.container}>
      <View style={styles.title}>
        <BodyText sizeMatch={['14']} bold>
          <FormattedMessage {...messages.title} />
        </BodyText>
      </View>

      {flexPaymentMethod ? (
        <View style={[styles.paymentMethod, styles.borderBottom]}>
          <View style={styles.info}>
            <Icon name="IconFlexCard" color="rgba(11, 0, 119, 1)" width={32} height={32} />

            <BodyText style={styles.text} sizeMatch={['14']}>
              {flexPaymentMethod}
            </BodyText>
          </View>

          <Switch
            size="large"
            disabled={isDisabled}
            accessibilityLabel={formatMessage(messages.changeHsaFsaUsage)}
            value={shouldUseFlex}
            onChange={onChangeFlexUsage}
          />
        </View>
      ) : null}

      {availableCredit > 0 ? (
        <View style={[styles.paymentMethod, styles.borderBottom]}>
          <View style={styles.info}>
            <Icon name="IconCredit" color={theme.colors.SPINACH} width={32} height={32} />

            <View>
              <BodyText style={styles.text} sizeMatch={['14']}>
                <FormattedMessage {...messages.creditUsageTitle} />
              </BodyText>

              <BodyText sizeMatch={['12']}>
                <FormattedMessage {...messages.creditUsageHint} values={{ applied, available }} />
              </BodyText>
            </View>
          </View>

          <Switch
            size="large"
            disabled={isDisabled}
            accessibilityLabel={formatMessage(messages.changeCreditUsage)}
            value={shouldUseCredit}
            onChange={onChangeCreditUsage}
          />
        </View>
      ) : null}

      <TouchableOpacity
        style={styles.paymentMethod}
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.changePaymentMethod)}
        disabled={isDisabled}
        onPress={onPressStripePaymentMethod}
      >
        <View style={styles.info}>
          <StripePaymentMethodImage
            imageBase64={stripePaymentMethodImage}
            icon={stripePaymentMethodIcon}
          />

          <BodyText style={styles.text} sizeMatch={['14']}>
            {hasNoPaymentMethod ? (
              stripePaymentMethod
            ) : (
              <FormattedMessage
                {...messages.paymentMethod}
                values={{ card: stripePaymentMethod }}
              />
            )}
          </BodyText>
        </View>

        <Icon name="IconArrowRight" color={theme.colors.SPINACH} width={24} height={24} />
      </TouchableOpacity>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useCreditUsageHint = (appliedCredit: number, availableCredit: number) => {
  const { formatNumber } = useIntl();

  const available = formatNumber(Number(availableCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  const applied = formatNumber(Number(appliedCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  return { applied, available };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Review your payment methods',
    description: 'Bag | Payment Methods | Title',
  },
  changeHsaFsaUsage: {
    defaultMessage: 'Change HSA/FSA usage',
    description: 'Bag | Payment Methods | Change HSA/FSA usage',
  },
  changeCreditUsage: {
    defaultMessage: 'Change credit usage',
    description: 'Bag | Payment Methods | Change credit usage',
  },
  changePaymentMethod: {
    defaultMessage: 'Change payment method',
    description: 'Bag | Payment Methods | Change payment method',
  },
  creditUsageTitle: {
    defaultMessage: 'SG Credit',
    description: 'Bag | Payment Methods | Credit usage title',
  },
  creditUsageHint: {
    defaultMessage: 'Use {applied} of {available} credits',
    description: 'Bag | Payment Methods | Credit usage hint',
  },
  paymentMethod: {
    defaultMessage: 'Default - {card}',
    description: 'Bag | Payment Methods | Payment method',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.large,
    borderColor: theme.colors.LINEN,
    borderWidth: 1,
  },
  title: {
    padding: theme.spacing['6'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
  paymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
  info: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  text: {
    color: theme.colors.SPINACH,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagPaymentMethodsProps = {
  isDisabled?: boolean;

  // HSA/FSA section.

  flexPaymentMethod?: string | null;
  shouldUseFlex: boolean;
  onChangeFlexUsage: (shouldUseFlex: boolean) => void;

  // Credit section.

  shouldUseCredit: boolean;
  availableCredit: number;
  appliedCredit: number;
  onChangeCreditUsage: (shouldUseCredit: boolean) => void;

  // Stripe section.

  hasNoPaymentMethod: boolean;
  stripePaymentMethod: string | undefined;
  stripePaymentMethodIcon: IconName | undefined;
  stripePaymentMethodImage: string | undefined;
  onPressStripePaymentMethod?: () => void;
};
